import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteFile } from '@/features/knowledge-bases/api/knowledgeBases.api';
import { notificationService } from '@/lib/services/notification.service';
import type { FileRow } from '@/features/knowledge-bases/model';
import { useFileUploadActions, useUploadsForKnowledgeBase } from '@/features/knowledge-bases/model';
import { useAuth } from '@/entities/user';
import { mapUploadItemToFileRow } from '@/features/knowledge-bases/lib/file-row-mappers';

interface UseKnowledgeBaseFileActionsProps {
  knowledgeBaseId: string | null;
  updatedFiles?: FileRow[];
  onUploadSuccess?: (files: File[]) => void;
  onUploadError?: (error: string) => void;
  onDeleteSuccess?: (fileId: string) => void;
  onDeleteError?: (error: string) => void;
}

interface UseKnowledgeBaseFileActionsReturn {
  // Delete state
  isDeleting: boolean;

  // Files
  allFiles: FileRow[];

  // Actions
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
}

export const useKnowledgeBaseFileActions = ({
  knowledgeBaseId,
  updatedFiles = [],
  onUploadSuccess,
  onUploadError,
  onDeleteSuccess,
  onDeleteError,
}: UseKnowledgeBaseFileActionsProps): UseKnowledgeBaseFileActionsReturn => {
  const { t } = useTranslation('knowledge');
  const uploadsForKnowledgeBase = useUploadsForKnowledgeBase(knowledgeBaseId || '');
  const signedInUser = useAuth((state) => state.signedInUser.value);

  const { addFilesToQueue, uploadAllForKnowledgeBase, clearCompletedUploads } =
    useFileUploadActions();

  const [isDeleting, setIsDeleting] = useState(false);

  const handleUpload = useCallback(
    async (filesToUpload: File[]) => {
      if (filesToUpload.length === 0) return;

      if (!knowledgeBaseId) {
        throw new Error('Knowledge base ID is required');
      }

      try {
        addFilesToQueue(knowledgeBaseId, filesToUpload);
        await uploadAllForKnowledgeBase(knowledgeBaseId);
        onUploadSuccess?.(filesToUpload);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t('knowledgeBases.useKnowledgeBaseFileActions.uploadFailed');
        notificationService.showError(errorMessage);
        onUploadError?.(errorMessage);
      } finally {
        clearCompletedUploads();
      }
    },
    [
      knowledgeBaseId,
      addFilesToQueue,
      uploadAllForKnowledgeBase,
      clearCompletedUploads,
      onUploadSuccess,
      onUploadError,
      t,
    ],
  );

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      if (!knowledgeBaseId) {
        throw new Error('Knowledge base ID is required');
      }

      if (!fileId.trim()) {
        const errorMessage = t('knowledgeBases.useKnowledgeBaseFileActions.fileIdIsRequired');
        notificationService.showError(errorMessage);
        return;
      }

      setIsDeleting(true);

      try {
        await deleteFile(knowledgeBaseId, fileId);
        notificationService.showSuccess(
          t('knowledgeBases.useKnowledgeBaseFileActions.documentDeletedSuccessfully'),
        );
        onDeleteSuccess?.(fileId);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t('knowledgeBases.useKnowledgeBaseFileActions.deleteFailed');
        notificationService.showError(errorMessage);
        onDeleteError?.(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    },
    [knowledgeBaseId, onDeleteSuccess, onDeleteError, t],
  );

  const allFiles = useMemo(() => {
    const currentUploads = uploadsForKnowledgeBase.map((upload) =>
      mapUploadItemToFileRow(upload, signedInUser?.id, signedInUser?.name),
    );
    return [...currentUploads, ...updatedFiles];
  }, [uploadsForKnowledgeBase, signedInUser?.id, signedInUser?.name, updatedFiles]);

  return {
    isDeleting,
    allFiles,
    uploadFiles: handleUpload,
    deleteFile: handleDeleteFile,
  };
};
